// src/components/intake/IntakeResults.js
//
// Ticket 069 (Advisor Intake I7), screen 7.1. Draws the blocks that arrive from
// intake-calculate: IRPF estimate (or why there is none), autónomo numbers, the
// Snapshot findings and timeline, the "not assessed" box and the Save bar. NO
// policy here: whether the estimate is shown at all was decided by the server
// (clientVisibility.ts); this only reads what is present.
//
// Layout (ticket 069, A1): two tabs, "Overall Picture" (default) and "IRPF Estimate",
// inside the shared ResultsLayout; the header, skipped-income note and "not assessed"
// box sit above the tabs; Save and Review live in the desktop rail and the mobile bar.
// The active tab is local UI state (not the reducer, not stored).
//
// Ticket 070 (I8): `share` (null when the client has no linked advisor) fills the rail slot
// and the mobile bar with the share entry, and adds the "saving also updates" line to the
// Save control while sharing. Presentational: the page owns all share state and calls.
//
// Shared pieces (moved or reused, not copied): IrpfEstimateBody
// (components/calculator/IrpfResultPanels.js), AutonomoSummaryBlock, SnapshotFinding
// (openLinksInNewTab), SnapshotTimeline, DisclaimerBanner, buildSnapshotFindings.

import React, { useState } from 'react'
import ResultsLayout from '../results/ResultsLayout'
import { IrpfEstimateBody } from '../calculator/IrpfResultPanels'
import { AutonomoSummaryBlock } from '../calculator/AutonomoSummaryBlock'
import SnapshotFinding from '../SnapshotFinding'
import SnapshotTimeline from '../SnapshotTimeline'
import DisclaimerBanner from '../DisclaimerBanner'
import buildSnapshotFindings from '../../lib/buildSnapshotFindings'
import {
  RESULTS_TITLE, RESULTS_ESTIMATE_NOTE, RESULTS_RATE_YEAR_LINE, RESULTS_ESTIMATE_HEADING,
  RESULTS_SNAPSHOT_HEADING, RESULTS_AUTONOMO_HEADING, RESULTS_STANDOUT_HEADING,
  RESULTS_ESTIMATE_BLOCKED_TITLE, RESULTS_FINDINGS_STILL_APPLY, RESULTS_SKIPPED_INCOME_NOTE,
  RESULTS_NOT_ASSESSED_TITLE, RESULTS_NOT_ASSESSED_BODY, RESULTS_NOT_ASSESSED_FIX_LABEL,
  RESULTS_TAB_OVERALL, RESULTS_TAB_IRPF, RESULTS_STATUS_UNSAVED, RESULTS_STATUS_SAVED,
  RESULTS_REVIEW_LABEL, RESULTS_SAVE_LABEL, RESULTS_SAVING_LABEL, RESULTS_SAVED_TEXT, RESULTS_SAVE_FAILED,
  SHARE_ENTRY_SAVE_FIRST, SHARE_ENTRY_LABEL, SHARE_ENTRY_SHARED, SHARE_ENTRY_LATER_SAVES, SHARE_SAVE_UPDATES_LINE,
  SHARE_STOP_LABEL, shareDateText,
} from '../../lib/intake/intakeConfig'

function EstimateSection({ results, estimate }) {
  if (estimate.kind === 'estimate') {
    return (
      <section className="intake-results-block">
        <h2 className="intake-results-heading">{RESULTS_ESTIMATE_HEADING}</h2>
        <IrpfEstimateBody result={results.irpf.result} filingComparison={results.irpf.filing_comparison} />
      </section>
    )
  }
  if (estimate.kind === 'blocked') {
    return (
      <section className="intake-results-block">
        <h2 className="intake-results-heading">{RESULTS_ESTIMATE_BLOCKED_TITLE}</h2>
        {estimate.reasons.map((reason) => (
          <p className="cal-blocked-reason" key={reason}>{reason}</p>
        ))}
        <p className="u-hint">{RESULTS_FINDINGS_STILL_APPLY}</p>
      </section>
    )
  }
  if (estimate.kind === 'hidden') {
    return (
      <section className="intake-results-block">
        <p className="intake-notice">{estimate.notice}</p>
      </section>
    )
  }
  return null
}

function SnapshotSection({ results }) {
  const facts = results.snapshot.derived_facts || {}
  const flags = results.snapshot.flags || []
  const taxYear = facts.taxYear || results.meta.facts_tax_year
  const { findings, notableChips, prose, timelineNodes } = buildSnapshotFindings(facts, flags, taxYear)
  return (
    <section className="intake-results-block">
      <h2 className="intake-results-heading">{RESULTS_SNAPSHOT_HEADING}</h2>
      <div className="trc-standout">
        <div className="trc-standout-eyebrow">
          <span className="trc-standout-icon">✦</span>
          <div className="u-eyebrow">{RESULTS_STANDOUT_HEADING}</div>
        </div>
        {notableChips ? (
          <div className="trc-standout-chips">
            {notableChips.map((chip) => <span className="trc-standout-chip" key={chip}>{chip}</span>)}
          </div>
        ) : (
          <p className="trc-standout-prose">{prose}</p>
        )}
      </div>
      <div className="trc-findings-card">
        {findings.map((finding, i) => (
          <SnapshotFinding key={finding.id} index={i + 1} finding={finding} openLinksInNewTab />
        ))}
      </div>
      {timelineNodes.length > 0 && <SnapshotTimeline nodes={timelineNodes} taxYear={taxYear} />}
      <DisclaimerBanner variant="banner" className="trc-disclaimer" />
    </section>
  )
}

const TABS = [
  { id: 'overall', label: RESULTS_TAB_OVERALL },
  { id: 'irpf', label: RESULTS_TAB_IRPF },
]

// The share entry (ticket 070): one small block in the rail and the mobile bar. The
// parent passes `share` only when the server named a linked advisor; with none, this
// renders nothing. Every step happens on the share screen (IntakeShare).
function ShareEntry({ share, saved, compact = false }) {
  if (!share) return null
  const { advisorName, shared, sharedAt } = share
  return (
    <div className={`intake-share-entry${compact ? ' intake-share-entry--compact' : ''}`}>
      {shared ? (
        <>
          <p className="intake-share-text u-hint">
            {SHARE_ENTRY_SHARED(advisorName, shareDateText(sharedAt))} {SHARE_ENTRY_LATER_SAVES(advisorName)}
          </p>
          <button type="button" className="btn-secondary" onClick={share.onOpen}>{SHARE_STOP_LABEL}</button>
        </>
      ) : saved ? (
        <button type="button" className="btn-secondary" onClick={share.onOpen}>{SHARE_ENTRY_LABEL(advisorName)}</button>
      ) : (
        <p className="intake-share-text u-hint">{SHARE_ENTRY_SAVE_FIRST(advisorName)}</p>
      )}
    </div>
  )
}

function SaveControls({ view, saving, saveError, onSave, onReview, share = null, compact = false }) {
  return (
    <div className={`intake-results-save${compact ? ' intake-results-save--compact' : ''}`}>
      <p className="intake-results-status" role="status">
        {view.saved ? RESULTS_STATUS_SAVED : RESULTS_STATUS_UNSAVED}
      </p>
      {view.saved && !compact && <p className="u-hint">{RESULTS_SAVED_TEXT}</p>}
      {saveError && <p className="intake-limit-note" role="alert">{RESULTS_SAVE_FAILED}</p>}
      {share && share.shared && <p className="u-hint intake-share-updates">{SHARE_SAVE_UPDATES_LINE(share.advisorName)}</p>}
      <div className="intake-results-actions">
        <button type="button" className="btn-primary" disabled={!view.canSave} onClick={onSave}>
          {saving ? RESULTS_SAVING_LABEL : RESULTS_SAVE_LABEL}
        </button>
        <button type="button" className="btn-secondary" onClick={onReview}>{RESULTS_REVIEW_LABEL}</button>
      </div>
    </div>
  )
}

export default function IntakeResults({ results, view, saving, saveError, onSave, onReview, onFix, onTab, share = null }) {
  const [tab, setTab] = useState('overall')

  function changeTab(id) {
    if (id === tab) return
    setTab(id)
    if (onTab) onTab(id)
  }

  const above = (
    <>
      <h1 className="triage-question">{RESULTS_TITLE}</h1>
      <p className="triage-hint">
        {RESULTS_ESTIMATE_NOTE}
        {view.rateYear ? ` ${RESULTS_RATE_YEAR_LINE(view.rateYear)}` : ''}
      </p>

      {view.incomeSkipped && <p className="intake-notice">{RESULTS_SKIPPED_INCOME_NOTE}</p>}

      {view.notAssessed.length > 0 && (
        <section className="intake-not-assessed" aria-label={RESULTS_NOT_ASSESSED_TITLE}>
          <h2 className="intake-results-heading">{RESULTS_NOT_ASSESSED_TITLE}</h2>
          <p className="u-hint">{RESULTS_NOT_ASSESSED_BODY}</p>
          <ul className="intake-not-assessed-list">
            {view.notAssessed.map((line) => (
              <li className="intake-not-assessed-row" key={line.screen}>
                <div className="intake-review-text">
                  <span className="intake-review-label">{line.label}</span>
                  <span className="u-hint">{line.impact}</span>
                </div>
                <button type="button" className="btn-secondary" aria-label={`${RESULTS_NOT_ASSESSED_FIX_LABEL}: ${line.label}`} onClick={() => onFix(line.screen)}>
                  {RESULTS_NOT_ASSESSED_FIX_LABEL}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  )

  const controls = { view, saving, saveError, onSave, onReview, share }

  return (
    <div className="intake-results">
      <ResultsLayout
        tabs={TABS}
        active={tab}
        onTab={changeTab}
        above={above}
        rail={(
          <div className="results-layout-rail-inner">
            <SaveControls {...controls} />
            <ShareEntry share={share} saved={view.saved} />
          </div>
        )}
        mobileBar={(
          <>
            <ShareEntry share={share} saved={view.saved} compact />
            <SaveControls {...controls} compact />
          </>
        )}
      >
        {tab === 'overall' ? (
          <SnapshotSection results={results} />
        ) : (
          <>
            {view.hasAutonomo && results.irpf && (
              <section className="intake-results-block">
                <h2 className="intake-results-heading">{RESULTS_AUTONOMO_HEADING}</h2>
                <AutonomoSummaryBlock
                  autonomoResult={results.autonomo.result}
                  autonomoIrpfImpact={results.autonomo.irpf_impact}
                  result={results.irpf.result}
                  hasPersonalFacts
                />
              </section>
            )}
            <EstimateSection results={results} estimate={view.estimate} />
          </>
        )}
      </ResultsLayout>
    </div>
  )
}
